import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, Typography, Tooltip, TextField, TablePagination, IconButton, CircularProgress} from '@mui/material';
import {makeStyles} from '@material-ui/core/styles';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    assignLicensesService, getOrgDetailsService,
    createClientService, editClientService, getLinkedAssessmentService,
    linkAssessmentService,
    listClientsService,
    publishClientService,
    resendClientActivationService
} from "../../services/client.service";
import CreateClientDialog from "./components/CreateClientDialog.comp";
import LoaderWithBackDrop from "../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {toast} from "../../utils/utils";
import {TOAST_TYPES, PRODUCT_KEY, LINK_TYPE_TO_PRODUCT_KEY, LINK_TYPE_TO_ACCESS_PERMISSIONS} from "../../utils/constants";
import {canLinkProduct, canCreateProduct, solutionPlanKey, isLinkingFreeOnPlan} from "../../utils/productAccess";
import {setOrgOverridesService} from "../../services/pac.service";
import AssignLicensesDialog from "./components/AssignLicensesDialog.comp";
import LinkAssessmentDialog from "./components/LinkAssessmentDialog.comp";
import {getInsiderAssessmentsService} from "../../services/assessments.service";
import {getUserDetailsService, updateUserDetailsService} from "../../services/auth.service";
import {getCountries, useGeo} from "../../utils/geo";
import {planHasSequentialSteps, mergeOnboardingRefreshIntoAuthUser} from "./DQProV2/constants/onboardingSteps.config";
import LinkProgramDialog from "./components/LinkProgramDialog.comp";
import {
    getInsiderProgramsService,
    getLinkedLearningProgramService,
    linkLearningProgramService
} from "../../services/learning_programs.service";
import EditClientDialog from "./components/EditClientDialog.comp";
import LinkTypePickerDialog, { LINK_TYPE } from "./components/LinkTypePickerDialog.comp";
import { AssignProductDialog, ConfirmAssignmentDialog } from "./components/LinkProductDialogs.comp";
import AddSchoolToNetworkDialog from "./components/AddSchoolToNetworkDialog.comp";
import BulkPublishSchoolsDialog from "./components/BulkPublishSchoolsDialog.comp";
import BulkUploadSchoolsDialog from "./components/BulkUploadSchoolsDialog.comp";
import MySchoolsGuide from "./components/MySchoolsGuide.comp";
import { UPLOAD_KIND_NETWORK, UPLOAD_KIND_PARTNER, buildSchoolsExportBlob, schoolsExportFileName } from "../../utils/schoolsUploadExcel";
import { saveTemplateBlob } from "../../utils/importTemplates";
import {useHistory} from 'react-router-dom';
import {getCreditBalanceService, getDqtWalletService} from "../../services/billing.service";
import AssignWithTokensDialog from "./components/AssignWithTokensDialog.comp";
import AccountLifecycleBadge, {isAccountDraft, isAccountPublished} from "./components/AccountLifecycleBadge.comp";
import {
    ConfirmPublishDialog,
    PublishWorkspaceSuccessDialog
} from "./components/PublishWorkspaceDialogs.comp";
import { useAuth } from "../../context/auth";
import NotFound from "../404/404.page";
import { USER_ACCESS_LEVEL, ENTERPRISE_PLAN_KEYS } from "../../utils/constants";

const ORANGE = 'var(--brand-primary, #FF6900)';

/**
 * The LICENSES cell — "88 used / 300 total" over a thin progress bar, as the
 * mock-up on slide 33 draws it. A network row also carries the allocated
 * figure, which is the share already handed down to its schools.
 */
const LicensesCell = ({ used = 0, allocated = null, total = 0, small = false }) => {
    const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    return (
        <Box style={{ minWidth: 0, textAlign: 'center' }}>
            <Typography style={{ fontSize: small ? 12.5 : 13.5, color: 'var(--brand-text, #444)' }}>
                <span style={{ fontWeight: 700, color: ORANGE }}>{used}</span>
                {' used / '}
                {allocated !== null && (<>{allocated}{' allocated / '}</>)}
                <span style={{ fontWeight: 700 }}>{total}</span>{' total'}
            </Typography>
            <Box style={{
                height: 4, borderRadius: 4, marginTop: 5,
                backgroundColor: 'rgba(241, 90, 36, 0.14)', overflow: 'hidden',
            }}>
                <Box style={{ width: pct + '%', height: '100%', backgroundColor: ORANGE }} />
            </Box>
        </Box>
    );
};

// Hover-text for each row action, taken from the button table on PW deck slide
// 34 ("Change the button titles") — that table, not the mock-up screenshot,
// is what defines the wording.
const ACTION_TOOLTIPS = {
    editDetails: 'Update the school or network information.',
    publish: 'Activates the school or network workspace and makes it available to the designated users.',
    assignLicenses: 'Assign learner licenses and LearnCreator credits to the school or network.',
    // Rows 4 and 5 of the button table — Link Products (LearnCreator) split
    // out from Link Programs (assessments/learning programs), replacing the
    // old combined wording above.
    linkProducts: 'Give the school or network access to LearnCreator.',
    linkPrograms: 'Give the school or network access to assessments or learning programs.',
};

const useStyles = makeStyles((theme) => ({
    tableContent: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
    },
    tableHeader: {
        display: 'grid',
        // The Actions column keeps the width of its buttons; the rest give way (Yufei-PW-05Oct-3).
        gridTemplateColumns: 'minmax(150px,2fr) minmax(0,2.2fr) minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,1.2fr) minmax(0,1.7fr) minmax(172px,2.2fr)',
        padding: '0 22px',
        border: '1px solid transparent',
        columnGap: 14,
        marginBottom: '12px',
        // Was 1120px, wider than a 1366px laptop screen leaves beside the menu.
        minWidth: '820px',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    headerCell: {
        fontSize: '13.5px !important',
        whiteSpace: 'nowrap',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
    },
    row: {
        display: 'grid',
        // The Actions column keeps the width of its buttons; the rest give way (Yufei-PW-05Oct-3).
        gridTemplateColumns: 'minmax(150px,2fr) minmax(0,2.2fr) minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,1.2fr) minmax(0,1.7fr) minmax(172px,2.2fr)',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '16px 22px',
        columnGap: 14,
        alignItems: 'center',
        marginBottom: '10px',
        // Was 1120px, wider than a 1366px laptop screen leaves beside the menu.
        minWidth: '820px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            padding: '12px 16px',
            gap: 0,
        },
    },
    cellText: {
        fontSize: '14px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: { fontSize: '13px !important' },
    },
    cellTextLeft: {
        fontSize: '14px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'left',
        [theme.breakpoints.down('sm')]: { fontSize: '13px !important' },
    },
    primaryBtn: {
        background: `${ORANGE} !important`,
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '5px 16px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '12px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': { background: '#c9541e !important' },
    },
    outlineBtn: {
        border: `1px solid ${ORANGE} !important`,
        color: `${ORANGE} !important`,
        background: 'var(--brand-surface, #fff) !important',
        borderRadius: '50px !important',
        padding: '5px 16px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '12px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': { background: 'var(--brand-surface, #fff7f4) !important' },
        '&.Mui-disabled': { borderColor: '#ccc !important', color: '#ccc !important' },
    },
    addBtn: {
        background: `${ORANGE} !important`,
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '10px 32px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '15px !important',
        boxShadow: 'none !important',
        '&:hover': { background: '#c9541e !important' },
    },
    /* The header Add button at the outline buttons' height: same padding, font
       size and a 1px border, so the two sit level. */
    addBtnCompact: {
        border: `1px solid ${ORANGE} !important`,
        padding: '5px 16px !important',
        fontSize: '12px !important',
        whiteSpace: 'nowrap',
    },
    /* Slide 33's "Schools in <network>" panel, tucked under its network row. */
    networkPanel: {
        margin: '-4px 0 10px 24px',
        padding: '14px 18px',
        borderRadius: '14px',
        backgroundColor: 'var(--brand-surface-muted, #FAF7F5)',
        border: '1px solid rgba(241, 90, 36, 0.14)',
        minWidth: '780px',
        [theme.breakpoints.down('sm')]: { minWidth: 0, marginLeft: 12 },
    },
    networkPanelHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 10,
    },
    // Same format as the main list header (headerCell) — sentence case, bold,
    // dark — per the PW deck ("Match the format as above").
    panelHeadCell: {
        fontSize: '13.5px !important',
        whiteSpace: 'nowrap',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
    },
    networkPanelRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0,2.1fr) minmax(0,2.3fr) minmax(0,1.4fr) minmax(0,0.9fr) minmax(0,1.2fr) minmax(0,1.6fr)',
        gap: 12,
        alignItems: 'center',
        padding: '10px 0',
        borderTop: '1px solid rgba(241, 90, 36, 0.10)',
        [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr', gap: 4 },
    },
    /* SNW deck slide 20: seven figures across the top of the Network school list
       (six school/license figures plus the network's own remaining credits). */
    statRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 12,
        marginBottom: 20,
        [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
        [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    },
    statCard: {
        border: '1px solid rgba(241, 90, 36, 0.18)',
        borderRadius: '14px',
        backgroundColor: 'var(--brand-surface, #fff)',
        padding: '14px 16px',
    },
    statValue: {
        fontSize: '26px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #111827)',
        lineHeight: 1.1,
    },
    statLabel: {
        fontSize: '12px !important',
        color: 'var(--brand-text-muted, #9CA3AF)',
        marginTop: 2,
    },
    /* Network table — SCHOOL · CONTACT · LOCATION · CLASSES · STUDENTS · PROGRAMS · LICENSES · ACTIONS */
    netHeader: {
        display: 'grid',
        // The Actions column keeps room for its two buttons a row; the rest give way.
        gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,2fr) minmax(0,1.1fr) minmax(0,0.7fr) minmax(0,0.8fr) minmax(0,1.1fr) minmax(0,1.4fr) minmax(216px,2.1fr)',
        padding: '0 22px',
        border: '1px solid transparent',
        columnGap: 14,
        marginBottom: '12px',
        // Was 1180px, wider than a laptop screen leaves beside the menu.
        minWidth: '840px',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    netRow: {
        display: 'grid',
        // The Actions column keeps room for its two buttons a row; the rest give way.
        gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,2fr) minmax(0,1.1fr) minmax(0,0.7fr) minmax(0,0.8fr) minmax(0,1.1fr) minmax(0,1.4fr) minmax(216px,2.1fr)',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '16px 22px',
        columnGap: 14,
        alignItems: 'center',
        marginBottom: '10px',
        // Was 1180px, wider than a laptop screen leaves beside the menu.
        minWidth: '840px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        [theme.breakpoints.down('sm')]: {
            display: 'flex', flexDirection: 'column', minWidth: 0, padding: '12px 16px', gap: 0,
        },
    },
    /*
     * The SNW row actions are a 2-up grid, not a stack (Ian-SNW-19Oct-2, Ian's
     * mockup "PbDQ_SNW_My Schools_New Guidance"):
     *   [ Edit Details ]  [ Assign Licenses ]
     *   [ Link Product ]  [ Link Programs   ]
     *   [        Manage Classes             ]
     * Publish Workspace and School Dashboard were removed from this row per
     * review feedback (see the comments at their call sites). Equal-width
     * cells stop the remaining buttons looking ragged.
     */
    netActions: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 5,
        alignContent: 'center',
    },
    netBtnWide: { gridColumn: '1 / -1' },
    netBtn: {
        width: '100%',
        padding: '4px 8px !important',
        fontSize: '11px !important',
        minHeight: '26px',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    desktopOnly: { [theme.breakpoints.down('sm')]: { display: 'none !important' } },
    /*
     * The row's stacked action buttons, all as wide as the widest one
     * ("Assign Licenses & Credits") instead of each hugging its own label.
     * One max-content column: every child stretches to it. The <span>s are the
     * Tooltip wrappers around buttons that can be disabled.
     */
    actionStack: {
        display: 'grid',
        gridTemplateColumns: 'max-content',
        justifyContent: 'center',
        alignContent: 'center',
        gap: 6,
        '& > button, & > span, & > span > button': { width: '100%' },
    },
    mobilePrimary: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
        },
    },
    mobilePrimaryText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--brand-text, #222)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileActions: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            gap: '6px',
            flexShrink: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
        },
    },
    mobileMetaRow: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, #f0f0f0)',
        },
    },
    mobileMetaTag: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--brand-text-muted, #555)',
            background: 'var(--brand-border, #f5f5f5)',
            borderRadius: '20px',
            padding: '2px 8px',
        },
    },
}));

const DQProClientList = () => {
    useGeo(); // re-render with the API's lists once they load
    const classes = useStyles();
    const { authUser, setAuthUser } = useAuth();
    const [loading, setLoading] = useState(true);
    /**
     * Whether the list has come back from the server at least once.
     *
     * The empty state is gated on this, not on `loading` — `loading` is shared
     * with every mutation on the page, so using it would blink the empty state
     * on each publish or licence assignment. Without the gate, a refresh shows
     * "No schools yet" and its two buttons for the length of the first request,
     * before the rows arrive.
     */
    const [listLoaded, setListLoaded] = useState(false);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [clientData, setClientData] = useState([]);
    // True while Export … Details reads every page of the list.
    const [exporting, setExporting] = useState(false);

    /*
     * All / Single schools / Networks — the filter pills on slide 33.
     * Applied to the rows already loaded, so it costs no extra request and the
     * server-side search and paging are untouched.
     */
    const [accountFilter, setAccountFilter] = useState('all');


    /* Credit balance for the footer strip on slides 33-34. */
    const [creditBalance, setCreditBalance] = useState(null);
    /*
     * The partner's DQ Token wallet — its real DQT balance and the plan's prices. A
     * partner on a PbDQ plan assigns licences and credits by converting DQT, with the
     * conversion shown live (AssignWithTokensDialog).
     */
    const [dqtWallet, setDqtWallet] = useState(null);
    /* Pill counts, computed server-side over the whole set (not the page). */
    const [counts, setCounts] = useState({ all: 0, single: 0, networks: 0 });
    const [createAsNetwork, setCreateAsNetwork] = useState(false);

    /* Slide 42 — adding a school to a network that already exists. */
    const [addToNetwork, setAddToNetwork] = useState(null);
    const [addingToNetwork, setAddingToNetwork] = useState(false);
    /* Networks whose school panel is expanded — slide 33's disclosure arrow. */
    const [expandedNetworks, setExpandedNetworks] = useState({});
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const debounceRef = useRef(null);

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignOrg, setAssignOrg] = useState({org_code: '', org_name: ''});
    const [assigning, setAssigning] = useState(false);
    /* A network's own licence pool — what its partner assigned it, less what is used or allocated. */
    const [networkPoolAvailable, setNetworkPoolAvailable] = useState(null);

    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    // institute_code: a school inside a network shares the network's org_code.
    const [linkOrg, setLinkOrg] = useState({org_code: '', org_name: '', institute_code: null});
    const [linking, setLinking] = useState(false);
    const [linkedAssessments, setLinkedAssessments] = useState([]);
    const [linkingLoading, setLinkingLoading] = useState(false);

    const [assessments, setAssessments] = useState([]);

    const [linkLearningDialogOpen, setLinkLearningDialogOpen] = useState(false);
    const [learningPrograms, setLearningPrograms] = useState([]);
    const [linkedLearningCodes, setLinkedLearningCodes] = useState([]);
    const [linkLearningLoading, setLinkLearningLoading] = useState(false);
    const [linkingLearning, setLinkingLearning] = useState(false);
    const [learningOrg, setLearningOrg] = useState({ org_code: '', org_name: '', institute_code: null });

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [editing, setEditing] = useState(false);

    const [linkProductsDialogOpen, setLinkProductsDialogOpen] = useState(false);
    const [linkProductsClient, setLinkProductsClient] = useState(null);
    const [chosenLinkType, setChosenLinkType] = useState(null);
    const [assignProductDialogOpen, setAssignProductDialogOpen] = useState(false);
    const [confirmAssignOpen, setConfirmAssignOpen] = useState(false);
    const [assigningProduct, setAssigningProduct] = useState(false);

    // "Link Programs" (slide 34, row 5) — Learning Program / Standard
    // Assessment only, no LearnCreator card (that's "Link Products" above).
    const [linkProgramsDialogOpen, setLinkProgramsDialogOpen] = useState(false);
    const [linkProgramsClient, setLinkProgramsClient] = useState(null);

    // Publication is a two-screen flow (SNW, SW Parental Consent Flow, slides 41
    // → 42): "Publish workspace?", then the success modal. There is no consent
    // step — the school or network sets up its own method once published.
    // `publishTarget` is the row being published.
    const [publishTarget, setPublishTarget] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [publishResult, setPublishResult] = useState(null);
    const [resending, setResending] = useState(false);
    // Header-level "Publish Workspace" (network only) — bulk-select any number
    // of the network's own not-yet-published schools, distinct from the
    // per-row single-school publish flow above.
    const [bulkPublishOpen, setBulkPublishOpen] = useState(false);
    // "Upload Excel" — schools (and networks, for a partner) from the approved
    // SNW / PW template. See BulkUploadSchoolsDialog.
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

    useEffect(() => {
        getInsiderAssessmentsService().then(res => {
            setAssessments(res?.data.assessments || []);
        });
    }, []);

    useEffect(() => {
        getInsiderProgramsService().then(res => {
            // learning_programs also carries unbuilt conversations; only a
            // built program can be linked to a client.
            const rows = res?.data.learning_programs || [];
            setLearningPrograms(rows.filter(p => p && p.is_completed !== false))
        });
    }, []);


    const fetchClients = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const response = await listClientsService({
                search: opts.search !== undefined ? opts.search : search,
                page:  (opts.page  !== undefined ? opts.page  : page) + 1,
                limit:  opts.limit !== undefined ? opts.limit : rowsPerPage,
                account_filter: opts.accountFilter !== undefined ? opts.accountFilter : accountFilter,
            });
            const data = (response && response.data) || {};
            setClientData(Array.isArray(data.clients) ? data.clients : []);
            setTotal(data.total || 0);
            setCounts(data.counts || { all: 0, single: 0, networks: 0 });
        } catch (err) {
            setClientData([]);
            setTotal(0);
            setCounts({ all: 0, single: 0, networks: 0 });
        }
        setListLoaded(true);
        setLoading(false);
    }, [search, page, rowsPerPage, accountFilter]);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    // Credit balance for the footer. Read-only and non-blocking: if it fails the
    // strip simply stays hidden rather than showing a number that might be wrong.
    useEffect(() => {
        let cancelled = false;
        getDqtWalletService()
            .then((res) => { if (!cancelled && res?.data) setDqtWallet(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        getCreditBalanceService()
            .then((res) => {
                const bal = res?.data?.balance_credits;
                if (!cancelled && typeof bal === 'number') setCreditBalance(bal);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Onboarding: visiting the School/Client List (the Step 2 "Accounts Setup"
    // card) marks Step 2 complete so Step 3 unlocks — same on-view pattern as
    // Steps 1 and 3. The create-client write below remains as a fallback.
    useEffect(() => {
        if (!planHasSequentialSteps(authUser?.dq_pro_plan?.plan_key)) return;
        // Already marked complete → skip. This effect previously ran on EVERY
        // visit, spending two SEQUENTIAL round-trips (write + /user/details
        // refresh) that could not change anything once Step 2 was done. The
        // write is idempotent, so skipping it is purely a saving.
        if ((authUser?.onboarding_steps_completed || []).includes(2)) return;
        updateUserDetailsService({ complete_onboarding_step: 2 })
            .then(() => getUserDetailsService())
            .then(res => {
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY onboarding progress — a full replace drops
                // login-time session fields (see mergeOnboardingRefreshIntoAuthUser).
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            fetchClients({ search: val, page: 0 });
        }, 400);
    };

    const handlePageChange = (_, newPage) => {
        setPage(newPage);
        fetchClients({ page: newPage });
    };

    const handleRowsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        fetchClients({ limit: newLimit, page: 0 });
    };

    /**
     * Is the person viewing this page a school network rather than the partner?
     *
     * The list is scoped server-side to that network's own schools, and a
     * network may only add a school to itself — Master Flow J02-012, Big Flow
     * row 13. So it gets "+ Add a School" and no Single/Network toggle.
     */
    const viewerIsNetwork = !!authUser?.is_school_network;
    // The PbDQ partner owner assigns with DQ Tokens; a network moves its own seats to
    // its schools with the plain dialog; older plans keep the plain dialog.
    const assignsWithTokens = !viewerIsNetwork && dqtWallet?.dqt_visible === true
        && String(dqtWallet?.plan_key || '').startsWith('enterprise');

    /*
     * A network assigns to its schools from the pool its partner gave it (a transfer,
     * never new seats — client_assignment.dqin.service assignLicencesGuarded). Read
     * fresh each time the pop-up opens; the same sum the API checks against.
     */
    const loadNetworkPool = useCallback(async () => {
        if (!viewerIsNetwork || !authUser?.org_code) return;
        // Not reset to null first: the Remaining licenses card reads this too,
        // and blanking it on every re-read made the card flash. The API still
        // refuses more than the pool holds, whatever the pop-up last showed.
        try {
            const res = await getOrgDetailsService(authUser.org_code);
            const lic = res?.data?.org?.licenses_dq_assess;
            if (lic) setNetworkPoolAvailable(Math.max((lic.total || 0) - (lic.used || 0) - (lic.allocated || 0), 0));
        } catch (_) {
            // Unknown: the pop-up still assigns and the API refuses more than the pool holds.
        }
    }, [viewerIsNetwork, authUser?.org_code]);

    useEffect(() => {
        if (assignDialogOpen && viewerIsNetwork) loadNetworkPool();
    }, [assignDialogOpen, viewerIsNetwork, loadNetworkPool]);

    /*
     * The "Remaining licenses" card reads the SAME pool — bug sheet
     * Ian-SNW-29Sep-12: licences the partner assigned to the network never
     * showed there, because the card summed the school rows only, while the
     * Assign pop-up said "Licenses available: 100". Re-read whenever the list
     * reloads, so an assignment moves the card too.
     */
    useEffect(() => {
        if (viewerIsNetwork) loadNetworkPool();
    }, [viewerIsNetwork, loadNetworkPool, clientData]);

    /*
     * The network's own display name comes from its workspace_memberships
     * entry (workspace_name), not from an org_name/organization_name/
     * company_name guess — same lookup Main.comp.jsx uses for the workspace
     * switcher, matched on dq_insider_id (+ role/org_code, since a user can
     * hold two memberships for the same dq_insider_id).
     */
    const networkWorkspaceName = (() => {
        const memberships = Array.isArray(authUser?.workspace_memberships) ? authUser.workspace_memberships : [];
        const match =
            memberships.find(
                (m) => m.dq_insider_id === authUser?.dq_insider_id
                    && (m.role === 'peer') === (authUser?.is_peer === true)
                    && (m.org_code || null) === (authUser?.org_code || null),
            ) || memberships.find((m) => m.dq_insider_id === authUser?.dq_insider_id);
        return match?.workspace_name || null;
    })();

    // A direct plan (a school network that bought for itself) counts as its
    // enterprise plan here — without solutionPlanKey a direct network was shown
    // the Not Found page below instead of its schools.
    const isEnterprisePlan = ENTERPRISE_PLAN_KEYS.includes(solutionPlanKey(authUser?.dq_pro_plan?.plan_key)) && authUser?.dq_pro_plan?.status === 'active';
    // What the link pop-ups call the row being linked (Ian-PW,SNW,SW-19Oct-1).
    const linkTargetNoun = (org) => (!isEnterprisePlan ? 'client' : (org?.is_school_network ? 'school network' : 'school'));
    // Show "Link Learning Program" only when the Learning Program product is
    // enabled AND grants the link permission for this account (PAC-driven, set
    // from the admin portal / plan). Previously hard-hidden for P0 accounts;
    // now it appears for any account — including P0 — that has activated the
    // product, matching the assessment-link gating model.
    const canLinkLearningProgram = canLinkProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);

    /*
     * "Link Products" (the LearnCreator card) — has this row's client ALREADY
     * been granted the LearnCreator access level this card assigns?
     *
     * Read directly off the row's own product_access, as returned by
     * /dq_insider/dqin/client/list — no separate per-org fetch needed.
     *
     * Checking enabled alone is not enough: enabled is also true for LearnLinker
     * (view+link, create:false — what a school/network is SEEDED with, and what
     * linking a program already grants) and LearnViewer, so blocking on enabled
     * alone would wrongly block a legitimate LearnLinker -> LearnCreator upgrade.
     * Checking create alone is not enough either: disabling a product preserves
     * its permissions sub-document (see OrgProductAccessDialog's handleToggle),
     * so a REVOKED LearnCreator grant would still read permissions.create: true
     * while enabled: false, and would wrongly block a fresh re-assignment.
     * Only enabled && permissions.create === true uniquely means "this org
     * already has LearnCreator, right now" (LEARNING_PROGRAM_ACCESS_PERMISSIONS
     * gives create: true to LEARN_CREATOR alone, never LEARN_LINKER/LEARN_VIEWER).
     */
    const linkCreatorProductKey = LINK_TYPE_TO_PRODUCT_KEY[LINK_TYPE.LEARN_CREATOR];
    const hasLearnCreator = (client) => {
        const entry = client?.product_access?.[linkCreatorProductKey];
        return entry?.enabled === true && entry?.permissions?.create === true;
    };
    const linkProductsUnavailableReasons = hasLearnCreator(linkProductsClient)
        ? { [LINK_TYPE.LEARN_CREATOR]: `LearnCreator is already linked to ${linkProductsClient?.org_name || 'this organization'}.` }
        : {};
    /*
     * Who may hand a product to the rows on this page.
     *
     * The partner (level 20), for its own clients — and a SCHOOL NETWORK for the
     * schools inside it. The network half is not a widening: the server has
     * always allowed exactly this (actor_org_scope's "a network may act on a
     * school of its own network" branch), and the same endpoint REFUSES the
     * partner for a school inside a network — "this school's product access is
     * managed by its network, not the partner". Withholding the button here left
     * nobody at all able to give those schools LearnCreator: the partner was
     * refused by the API, the network was never offered the control, and a
     * sub-school is not even a top-level row on the partner's list.
     *
     * Safe because the network's list is scoped server-side to its own schools
     * (listClients' network_scope), so every row it can press this on is one it
     * is entitled to. The network cannot pass on access it does not itself hold
     * — setOrgOverrides measures a network's grant against the network's own
     * resolved access.
     *
     * Per-plan availability (P0/P1/LC) is handled inside LinkTypePickerDialog
     * via ENABLED_BY_PLAN, not here.
     */
    const canLinkProducts =
        authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
        || viewerIsNetwork;
    /*
     * A network can only pass on LearnCreator it holds itself (setOrgOverrides
     * checks the network's own row, per dimension). The session's product access
     * is that row's, so a LearnLinker network sees why, rather than a 400.
     */
    const networkHoldsLearnCreator = viewerIsNetwork
        && canCreateProduct(authUser, linkCreatorProductKey);
    const networkLinkProductTooltip = (client) => {
        if (!networkHoldsLearnCreator) return 'Your network does not have LearnCreator. Request it from your partner with Unlock a capability.';
        if (hasLearnCreator(client)) return `LearnCreator is already linked to ${client.org_name}.`;
        // No hover text when the button can be used (Ian-SNW-19Oct-1).
        return '';
    };
    if (authUser && !isEnterprisePlan && (authUser.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE || authUser.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE)) {
        return <NotFound />;
    }

    /**
     * Slide 42 — add one school to an existing network.
     * Same create endpoint; the dialog supplies network_org_code, which
     * the API consumes to build the new school's org_code.
     */
    const handleAddSchoolToNetwork = async (fields) => {
        setAddingToNetwork(true);
        setLoading(true);
        try {
            const response = await createClientService({ ...fields });
            if (response?.data?.success) {
                setAddToNetwork(null);
                toast(
                    TOAST_TYPES.SUCCESS,
                    "School Added to Network",
                    "Nothing is sent until you publish the workspace.",
                );
                await fetchClients();
            }
        } catch (err) {
            // invokeApi surfaces the backend message as a toast.
        } finally {
            setAddingToNetwork(false);
            setLoading(false);
        }
    };

    const handleCreateClient = async (fields) => {
        setCreating(true);
        setLoading(true)
        try {
            const payload = { ...fields };
            const response = await createClientService(payload);
            if (response?.data?.success) {
                setOpenCreateDialog(false);
                toast(
                    TOAST_TYPES.SUCCESS,
                    isEnterprisePlan ? "School Saved as Draft" : "Client Saved as Draft",
                    "Nothing is sent until you publish the workspace.",
                );
                // Jump to the first page (newest first) so the new client is visible on top.
                setSearch('');
                setPage(0);
                await fetchClients({ search: '', page: 0 });

                // Onboarding: creating a client is the real completion signal for
                // Step 2 ("Accounts Setup") — but only for plans with numbered/sequential
                // steps (currently just enterprise_p0). Fire-and-forget, doesn't block the UX.
                if (planHasSequentialSteps(authUser?.dq_pro_plan?.plan_key)) {
                    updateUserDetailsService({ complete_onboarding_step: 2 })
                        .then(() => getUserDetailsService())
                        .then(res => {
                            const refreshedUser = res?.data?.user || res?.data;
                            const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                            if (merged !== authUser) setAuthUser(merged);
                        })
                        .catch(() => {});
                }
            }
        } catch (err) {
            console.log('Failed to create client!');
        } finally {
            setCreating(false);
            setLoading(false);
        }
        return true;
    };

    /**
     * Save an edit. For a network the dialog also hands back the schools already
     * in it (slides 40-41), which are separate organisations — so they are saved
     * with the same endpoint, one call each, after the network itself.
     *
     * Not a transaction: an edit that fails half-way leaves the earlier schools
     * saved. That is why the failure toast names the school that stopped it, and
     * the list is refreshed either way so the screen shows what actually stuck.
     */
    const handleEditClient = async (formData, schools = []) => {
        setEditing(true);
        setLoading(true);
        let failedOn = null;
        try {
            const response = await editClientService(editingClient.org_code, formData);
            if (!response?.data?.success) {
                failedOn = editingClient.org_name;
            } else {
                for (const sc of schools) {
                    if (!sc.org_code) continue;
                    const { org_code, ...schoolFields } = sc;
                    const r = await editClientService(org_code, schoolFields);
                    if (!r?.data?.success) { failedOn = sc.org_name; break; }
                }
            }
            if (failedOn) {
                toast(TOAST_TYPES.DANGER, "Failed", "Could not save " + failedOn + ".");
            } else {
                toast(TOAST_TYPES.SUCCESS, "Success", "Client updated successfully");
                setEditDialogOpen(false);
            }
            await fetchClients();
        } catch (err) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to update client");
        } finally {
            setEditing(false);
            setLoading(false);
        }
    };

    const handleOpenEdit = (client) => {
        setEditingClient(client);
        setEditDialogOpen(true);
    };

    const handleOpenPublish = (client) => {
        setPublishTarget(client);
    };

    const handleClosePublishFlow = () => {
        setPublishTarget(null);
    };

    const handleConfirmPublish = async () => {
        if (!publishTarget) return;
        setPublishing(true);
        try {
            const response = await publishClientService(
                publishTarget.org_code,
                {
                    // Which school of the network — see the service. Undefined for
                    // a standalone account, which owns its org_code outright.
                    institute_code: publishTarget.institute_code,
                },
            );
            const data = response?.data;
            if (data?.success) {
                // Already published (a stale row, or a second click) is a success
                // with nothing sent — say that rather than claiming an email.
                setPublishResult({
                    org_code: publishTarget.org_code,
                    institute_code: publishTarget.institute_code,
                    org_name: publishTarget.org_name,
                    contact_email: data.contact_email || publishTarget.contact_email,
                    published_at: data.published_at,
                    credentials_emailed: !!data.credentials_emailed,
                    already_published: !!data.already_published,
                });
                handleClosePublishFlow();
                await fetchClients();
            }
        } catch (err) {
            // invokeApi surfaces the backend message as a toast.
        } finally {
            setPublishing(false);
        }
    };

    const handleResendActivation = async (org_code, institute_code) => {
        if (!org_code) return;
        setResending(true);
        try {
            const response = await resendClientActivationService(org_code, institute_code);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Sent', 'Activation email sent again.');
                // The send just succeeded, so correct the checklist if it was
                // showing the failed-send state.
                setPublishResult((prev) => (prev ? { ...prev, credentials_emailed: true } : prev));
            }
        } catch (err) {
            // invokeApi surfaces the backend message as a toast.
        } finally {
            setResending(false);
        }
    };

    const handleOpenAssign = (client) => {
        // institute_code travels with org_code — siblings share the code, and
        // only the institute says which school the licences are for.
        setAssignOrg({
            org_code: client.org_code,
            institute_code: client.institute_code,
            org_name: client.org_name,
            // The school's own figures, for the network's Assign licenses cards.
            licenses_total: client.licenses_total || 0,
            licenses_used: client.licenses_used || 0,
            // Credits are for LearnCreator: the pop-up locks them until it is
            // linked (LA-PW-12-Oct-03, slide 59 — the P0, P1A card).
            learncreator_linked: hasLearnCreator(client),
        });
        setAssignDialogOpen(true);
    };

    const handleAssignLicenses = async ({ org_code, license_count }) => {
        setAssigning(true);
        setLoading(true);
        try {
            const response = await assignLicensesService(org_code, license_count, assignOrg.institute_code);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Licenses Assigned", "Successfully assigned licenses!");
                setAssignDialogOpen(false);
                await fetchClients(); // Refresh list if needed
            }
        } catch (e) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to assign licenses!");
        } finally {
            setAssigning(false);
            setLoading(false);
        }
    };

    const handleLinkAssessments = async (orgCode, selectedCodes) => {
        setLinking(true);
        setLoading(true);
        try {
            const response = await linkAssessmentService(orgCode, selectedCodes, undefined, linkOrg.institute_code);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Success", "Assessments linked successfully");
                await fetchClients(); // refresh
            }
            setLinkDialogOpen(false);
        } catch (err) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to link assessments");
        } finally {
            setLinking(false);
            setLoading(false);
        }
    };

    const handleOpenLinkLearning = async (client) => {
        setLearningOrg({ org_code: client.org_code, org_name: client.org_name, institute_code: client.institute_code || null, is_school_network: !!client.is_school_network });
        setLinkLearningDialogOpen(true);
        setLinkLearningLoading(true);
        try {
            const res = await getLinkedLearningProgramService(client.org_code, client.institute_code || null);
            if (res?.data?.linked_learning_programs) {
                setLinkedLearningCodes(res.data.linked_learning_programs.map(a => a.code));
            } else {
                setLinkedLearningCodes([]);
            }
        } catch (err) {
            setLinkedLearningCodes([]);
        }
        setLinkLearningLoading(false);
    };

    const handleOpenLinkPrograms = (client) => {
        setLinkProgramsClient(client);
        setLinkProgramsDialogOpen(true);
    };

    const handleChooseProgramType = (linkType) => {
        setLinkProgramsDialogOpen(false);
        if (linkType === LINK_TYPE.LEARNING_PROGRAM) {
            handleOpenLinkLearning(linkProgramsClient);
        } else if (linkType === LINK_TYPE.STANDARD_ASSESSMENT) {
            handleOpenLink(linkProgramsClient);
        }
    };

    const handleOpenLinkProducts = (client) => {
        setLinkProductsClient(client);
        setLinkProductsDialogOpen(true);
    };

    const handleChooseProductType = (linkType) => {
        setChosenLinkType(linkType);
        setLinkProductsDialogOpen(false);
        setAssignProductDialogOpen(true);
    };

    const handleProceedToConfirmAssign = () => {
        setAssignProductDialogOpen(false);
        setConfirmAssignOpen(true);
    };

    const handleCancelConfirmAssign = () => {
        setConfirmAssignOpen(false);
        setAssignProductDialogOpen(true);
    };

    const handleConfirmAssignment = async () => {
        const productKey = LINK_TYPE_TO_PRODUCT_KEY[chosenLinkType];
        // The named access level this card hands over — LearnCreator, the only
        // one of the three that opens authoring. A card with no access level
        // mapped assigns nothing rather than falling back to an inherited
        // permission set, which is how `create` used to be granted by accident.
        const permissions = LINK_TYPE_TO_ACCESS_PERMISSIONS[chosenLinkType];
        if (!productKey || !permissions || !linkProductsClient) return;
        setAssigningProduct(true);
        try {
            // "Assign & notify" — the admin gets the access email (RQ-03).
            // institute_code: a network assigning to ONE of its schools — that
            // school's own access, not the network's (which every school shares
            // the org_code of).
            await setOrgOverridesService(linkProductsClient.org_code, {
                [productKey]: { enabled: true, permissions },
            }, true, linkProductsClient.institute_code || undefined);
            toast(TOAST_TYPES.SUCCESS, 'Assigned', `LearnCreator was enabled for ${linkProductsClient.org_name}.`);
            setConfirmAssignOpen(false);
            setLinkProductsClient(null);
            setChosenLinkType(null);
            // The row's product_access drives the button's "already linked" state.
            fetchClients();
        } catch (err) {
            toast(TOAST_TYPES.DANGER, 'Error', err?.message || 'Failed to assign LearnCreator.');
        } finally {
            setAssigningProduct(false);
        }
    };

    const handleOpenLink = async (client) => {
        setLinkOrg({org_code: client.org_code, org_name: client.org_name, institute_code: client.institute_code || null, is_school_network: !!client.is_school_network});
        setLinkDialogOpen(true);
        setLinkingLoading(true);
        setLoading(true);
        try {
            const res = await getLinkedAssessmentService(client.org_code, client.institute_code || null);
            if (res?.data?.linked_assessments) {
                setLinkedAssessments(res.data.linked_assessments.map(a => a.code));
            } else {
                setLinkedAssessments([]);
            }
        } catch (err) {
            setLinkedAssessments([]);
        } finally {
            setLinkingLoading(false);
            setLoading(false);
        }
    };

    const handleLinkLearningPrograms = async (orgCode, selectedCodes) => {
        setLinkingLearning(true);
        setLoading(true);
        try {
            const response = await linkLearningProgramService(orgCode, selectedCodes, learningOrg.institute_code);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Success", "Learning Programs linked successfully");
                await fetchClients(); // refresh
            }
            setLinkLearningDialogOpen(false);
        } catch (err) {
            // invokeApi already surfaces the backend error message as a toast
            // (e.g. "Cannot link unpublished learning program(s)... Publish the program before linking it to a client org.").
        } finally {
            setLinkingLearning(false);
            setLoading(false);
        }
    };


    const countryCodeToName = Object.fromEntries(
        getCountries().map(({ code, label }) => [code, label])
    );

    // Helper function to check if client has enough licenses to link assessments/programs
    /**
     * Does a licence shortage block "Link Programs" for this row? Never on the
     * PbDQ partner and direct plans — linking only makes a program available
     * to the level below (isLinkingFreeOnPlan). Legacy plans keep the check.
     */
    const linkBlockedByLicenses = (client) => !isLinkingFreeOnPlan(authUser) && !hasEnoughLicenses(client);

    const hasEnoughLicenses = (client) => {
        if (client.licenses_total === 0) {
            return false;
        }
        const licensesRemaining = client.licenses_total - client.licenses_used;
        return licensesRemaining >= client.users;
    };

    /**
     * "4 schools in 2 countries" for a network row — slide 33.
     * Reads client.schools, which the API attaches to network rows, so the count
     * is the whole network and not just whichever schools landed on this page.
     */
    const networkSummary = (client) => {
        const kids = client.schools || [];
        const n = kids.length;
        const cn = new Set(kids.map((c) => c.country).filter(Boolean)).size;
        const schoolWord = n === 1 ? 'school' : 'schools';
        const countryWord = cn === 1 ? 'country' : 'countries';
        return [n, schoolWord, 'in', cn, countryWord].join(' ');
    };

    /**
     * "Singapore +1 more" — slide 33. The API sends every country a row
     * covers; for a network that is the union of its schools’.
     */
    const locationLabel = (client) => {
        const list = (client.countries && client.countries.length)
            ? client.countries
            : [client.country].filter(Boolean);
        if (!list.length) return '—';
        const first = countryCodeToName[list[0]] || list[0];
        return list.length > 1 ? `${first} +${list.length - 1} more` : first;
    };

    const toggleNetwork = (org_code) => {
        setExpandedNetworks((m) => ({ ...m, [org_code]: !m[org_code] }));
    };

    /**
     * "3 credits = 1 license" on slide 33. Read off the plan rather than
     * hard-coded — the PbDQ sub-plans all price a user's assessment access at 3
     * credits, but the blueprint Enterprise plan leaves it null (volume
     * discounted, resolved at billing time) and must not claim a flat rate.
     *
     * STOPGAP: backend isn't yet sending cost_user_assessment_access_credits
     * on dq_pro_plan for at least enterprise_p1 (confirmed missing entirely,
     * not just falsy, via a live session's localStorage). Until that's fixed
     * server-side, fall back to the known flat rate (3) for any PbDQ sub-plan
     * (ENTERPRISE_PLAN_KEYS) — never for the bare blueprint ENTERPRISE plan,
     * which must keep showing no flat rate. Remove this fallback once the
     * backend field is populated for every sub-plan.
     */
    const PBDQ_FLAT_CREDITS_PER_LICENSE = 3;
    const creditsPerLicense = authUser?.dq_pro_plan?.cost_user_assessment_access_credits
        ?? (ENTERPRISE_PLAN_KEYS.includes(solutionPlanKey(authUser?.dq_pro_plan?.plan_key)) ? PBDQ_FLAT_CREDITS_PER_LICENSE : undefined);
    const redeemableLicenses = creditsPerLicense > 0 && creditBalance !== null
        ? Math.floor(creditBalance / creditsPerLicense)
        : 0;

    /**
     * "4 schools in 2 countries, one shared license pool" — SNW deck slide 20.
     * The network is looking at its OWN schools, so the counts come from the
     * rows themselves.
     */
    // Replaced on the page by the SNW deck's fixed subtitle; kept for reference.
    // const networkSubtitle = (() => {
    //     const n = total;
    //     const cn = new Set(clientData.map((c) => c.country).filter(Boolean)).size;
    //     const schoolWord = n === 1 ? 'school' : 'schools';
    //     const countryWord = cn === 1 ? 'country' : 'countries';
    //     return [n, schoolWord, 'in', cn, countryWord].join(' ') + ', one shared license pool';
    // })();

    /**
     * The six figures across the top of the Network school list — SNW deck
     * slide 20. Summed from the rows on this page; `total` is the whole set.
     */
    const netStats = {
        schools: total,
        classes: clientData.reduce((n, c) => n + (c.classes_count || 0), 0),
        students: clientData.reduce((n, c) => n + (c.users || 0), 0),
        licensesTotal: clientData.reduce((n, c) => n + (c.licenses_total || 0), 0),
        licensesUsed: clientData.reduce((n, c) => n + (c.licenses_used || 0), 0),
    };
    // A network's remaining licences are what it can still hand to its schools —
    // its own pool, exactly the "Licenses available" of the Assign pop-up. Null
    // (shown as "—") until that pool is read, never the schools' sum: that is a
    // different number, and showing it first made the card jump.
    netStats.licensesRemaining = viewerIsNetwork
        ? (typeof networkPoolAvailable === 'number' ? networkPoolAvailable : null)
        : Math.max(0, netStats.licensesTotal - netStats.licensesUsed);

    /**
     * Export … Details — EVERY row of the list, not only the page on screen.
     * The table is paged (10 at a time), so the export fetches the rest itself,
     * 100 at a time, under the same search and School/Network pill as the table.
     */
    const loadAllListedClients = async () => {
        const all = [];
        for (let p = 1; p <= 500; p += 1) {
            const res = await listClientsService({ search, page: p, limit: 100, account_filter: accountFilter });
            const rows = res?.data?.clients || [];
            all.push(...rows);
            if (rows.length < 100 || all.length >= (res?.data?.total || 0)) break;
        }
        return all;
    };

    const downloadSchoolsCsv = async () => {
        if (exporting) return;
        setExporting(true);
        let listed = clientData;
        try {
            listed = await loadAllListedClients();
        } catch (e) {
            // Could not read the rest: say so rather than hand over a short file.
            setExporting(false);
            toast(TOAST_TYPES.ERROR, 'Export', 'Could not export the full list. Please try again.');
            return;
        }
        /*
         * The upload template's own layout, filled in — the same file a
         * workspace downloads as its template, as Export Class Details is for
         * classes (8 Oct 2026). It was a CSV summary of the table before.
         */
        try {
            const kind = viewerIsNetwork ? UPLOAD_KIND_NETWORK : UPLOAD_KIND_PARTNER;
            // The import template itself, filled in (Ian, 13 Oct 2026).
            saveTemplateBlob(await buildSchoolsExportBlob(kind, listed), schoolsExportFileName(kind));
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Export', 'Could not build the export file. Please try again.');
        }
        setExporting(false);
    };

    // The server already applied the pill, so the page is what we render.
    const visibleClients = clientData;

    const handleAccountFilter = (key) => {
        setAccountFilter(key);
        setPage(0);
        fetchClients({ accountFilter: key, page: 0 });
    };

    /* Which mode the create dialog opens in — slide 33 offers both entry points. */
    /**
     * "+ Add a School".
     *
     * A PARTNER gets the generic create form — it registers a brand new school
     * or network that belongs to nobody yet.
     *
     * A NETWORK adding to ITSELF must not get that form. Network Workspace deck
     * slide 21 gives this its own dialog ("Add a school to your network"), and
     * more than wording hangs on it: the generic form does not set
     * network_org_code, so a school created through it is a standalone school
     * that merely looks like a member. It has to go through
     * AddSchoolToNetworkDialog with the viewer's own org as the parent.
     *
     * This is what Lydia reported as "this UI has not been updated in the
     * system" — the dialog existed, but only the per-row "+ Add a school" inside
     * an expanded network reached it. The header button, which is the only one a
     * network viewer has, went to the generic form.
     */
    // "Upload …" inside an Add dialog: close it and open the upload window.
    const canBulkUpload = viewerIsNetwork || isEnterprisePlan;
    // The Partner's pair is "Add Manually / Import CSV" (LA-PW-07-Oct-03, slide 10).
    const bulkUploadLabel = 'Import CSV';
    // "Add Manually" inside the import: close it and open the form again.
    const openAddFromBulkUpload = () => {
        setBulkUploadOpen(false);
        // The network's own Add form, or the partner's.
        openCreate(false);
    };
    const openBulkUploadFromAdd = () => {
        setOpenCreateDialog(false);
        setAddToNetwork(null);
        setBulkUploadOpen(true);
    };

    const openCreate = (network) => {
        if (viewerIsNetwork) {
            setAddToNetwork({
                org_code: authUser?.org_code,
                org_name: networkWorkspaceName,
            });
            return;
        }
        setCreateAsNetwork(!!network);
        setOpenCreateDialog(true);
    };


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {/* Network name above the title removed per the SNW deck ("Remove").
            {viewerIsNetwork && networkWorkspaceName && (
                <Typography style={{ fontSize: 15, fontWeight: 700, color: ORANGE, textAlign: 'center', marginBottom: 4 }}>
                    {networkWorkspaceName}
                </Typography>
            )}
            */}
            <Typography style={{ fontSize: 35, fontWeight: 800, marginBottom: 16, textAlign: 'center', color: 'var(--brand-text, #111)' }}>
                {viewerIsNetwork ? 'My Schools'
                    : (isEnterprisePlan ? 'My Schools' : 'Your Client List')}
            </Typography>
            <Typography style={{ fontSize: 15, color: 'var(--brand-text-muted, #888)', textAlign: 'center', marginBottom: 32 }}>
                {viewerIsNetwork
                    ? 'Manage your schools, assign licenses, and give them access to your programs.'
                    : (isEnterprisePlan
                        ? 'Manage your schools and networks, assign licenses, and give them access to your programs.'
                        : 'Add new clients, manage their products, and assign licenses.')}
            </Typography>

            <Box style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder={viewerIsNetwork ? "Search schools..." : (isEnterprisePlan ? "Search by name, contact, or email..." : "Search by name or email…")}
                    value={search}
                    onChange={handleSearchChange}
                    onFocus={() => setSearchFocused(true)}
                    name="client-list-filter"
                    type="search"
                    inputProps={{
                        autoComplete: 'off',
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                        readOnly: !searchFocused || openCreateDialog,
                    }}
                    // Wide enough for the whole placeholder; it no longer shrinks (Yufei-PW-05Oct-2).
                    style={{ flex: '0 0 340px', maxWidth: '100%' }}
                />
                {/* Everything after the search box clusters flush right,
                    instead of space-between spreading it across the row. */}
                <Box style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
                    {/*
                      * All / Single schools / Networks — slide 33. Both the filter
                      * and the counts are server-side: with five rows to a page,
                      * counting the loaded rows would under-report every pill.
                      */}
                    {isEnterprisePlan && !viewerIsNetwork && (
                        <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {[
                                { key: "all", label: "All", count: counts.all },
                                { key: "single", label: "Single schools", count: counts.single },
                                { key: "networks", label: "Networks", count: counts.networks },
                            ].map((p) => (
                                <Button
                                    key={p.key}
                                    onClick={() => handleAccountFilter(p.key)}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "50px",
                                        px: 2,
                                        py: 0.5,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        border: "1px solid",
                                        borderColor: accountFilter === p.key ? ORANGE : "var(--brand-border, #E5E7EB)",
                                        color: accountFilter === p.key ? ORANGE : "var(--brand-text-muted, #6B7280)",
                                        backgroundColor: accountFilter === p.key ? "rgba(255,105,0,0.08)" : "transparent",
                                    }}
                                >
                                    {p.label}&nbsp;&nbsp;{p.count}
                                </Button>
                            ))}
                        </Box>
                    )}
                    {/* "Export XX Details", as My Classes has Export Class Details
                        (Ian-SNW-30Sep-3; Ian / Lydia, 8 Oct 2026). A partner's list holds
                        networks as well as schools, so its label says so. */}
                    {(viewerIsNetwork || isEnterprisePlan) && (
                        <Button className={classes.outlineBtn} onClick={downloadSchoolsCsv} disabled={exporting} style={{ padding: '9px 20px' }}>
                            {/* A spinner and "Exporting …" while every page of the list is read. */}
                            {exporting && <CircularProgress size={14} thickness={5} style={{ color: 'inherit', marginRight: 8 }} />}
                            {exporting
                                ? (viewerIsNetwork ? 'Exporting School Details…' : 'Exporting School & Network Details…')
                                // "Export School & Network Details" — LA-PW-07-Oct-03, slide 16.
                                : (viewerIsNetwork ? 'Export School Details' : 'Export School & Network Details')}
                        </Button>
                    )}
                    {/* No separate upload button: it is offered inside Add (Ian, 8 Oct 2026). */}
                    {/* The partner's stays orange but takes the height of the button on
                        its left (LA-PW-07-Oct-03, slide 16). */}
                    {/* The network's header (Ian-SNW-07Oct2026-1, slide 46): Publish
                        Workspace before Add, both filled orange and the same size. */}
                    {viewerIsNetwork && (
                        <Button className={`${classes.addBtn} ${classes.addBtnCompact}`} style={{ minWidth: 180 }} onClick={() => setBulkPublishOpen(true)}>
                            Publish Workspace
                        </Button>
                    )}
                    <Button className={`${classes.addBtn} ${(viewerIsNetwork || isEnterprisePlan) ? classes.addBtnCompact : ''}`} style={viewerIsNetwork ? { minWidth: 180 } : undefined} onClick={() => openCreate(false)}>
                        {viewerIsNetwork ? '+ Add a School'
                            : (isEnterprisePlan ? '+ Add a School or Network' : '+ Add a New Client')}
                    </Button>
                </Box>
            </Box>

            {/* Slide 20: six figures, then the school list. */}
            {viewerIsNetwork && (
                <Box className={classes.statRow}>
                    {[
                        { v: netStats.schools, l: 'Schools' },
                        { v: netStats.classes, l: 'Classes' },
                        { v: netStats.students, l: 'Students' },
                        { v: netStats.licensesTotal, l: 'Total licenses' },
                        { v: netStats.licensesUsed, l: 'Used licenses' },
                        { v: netStats.licensesRemaining, l: 'Remaining licenses', accent: true },
                        ...(creditBalance !== null ? [{ v: creditBalance, l: 'Remaining credits' }] : []),
                    ].map((st) => (
                        <Box key={st.l} className={classes.statCard}>
                            <Typography className={classes.statValue} style={st.accent ? { color: ORANGE } : undefined}>
                                {st.v == null ? '—' : st.v.toLocaleString()}
                            </Typography>
                            <Typography className={classes.statLabel}>{st.l}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <Box className={classes.tableContent}>
                {visibleClients.length > 0 && !viewerIsNetwork && (
                    <Box className={classes.tableHeader}>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>School</Typography>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Contact</Typography>
                        <Typography className={classes.headerCell}>Location</Typography>
                        <Typography className={classes.headerCell}>Students</Typography>
                        <Typography className={classes.headerCell}>Programs</Typography>
                        <Typography className={classes.headerCell}>Licenses</Typography>
                        <Typography className={classes.headerCell}>Actions</Typography>
                    </Box>
                )}

                {/* Network view — SNW deck slide 20. Its own columns and actions. */}
                {viewerIsNetwork && visibleClients.length > 0 && (
                    <Box className={classes.netHeader}>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>School</Typography>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Contact</Typography>
                        <Typography className={classes.headerCell}>Location</Typography>
                        <Typography className={classes.headerCell}>Classes</Typography>
                        <Typography className={classes.headerCell}>Students</Typography>
                        <Typography className={classes.headerCell}>Programs</Typography>
                        <Typography className={classes.headerCell}>Licenses</Typography>
                        <Typography className={classes.headerCell}>Actions</Typography>
                    </Box>
                )}

                {viewerIsNetwork && visibleClients.map((client, index) => (
                    /* Keyed by the PAIR: every school of a network shares that
                       network's org_code, so org_code alone is the same string on
                       every sibling and React would see one duplicated key. */
                    <Box key={[client.org_code, client.institute_code].filter(Boolean).join('::') || index} className={classes.netRow}>
                        <Box className={classes.desktopOnly} style={{ minWidth: 0 }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <Typography className={classes.cellTextLeft} style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'normal' }}>
                                    {client.org_name}
                                </Typography>
                                <AccountLifecycleBadge status={client.lifecycle_status} />
                            </Box>
                        </Box>
                        <Box className={classes.desktopOnly} style={{ minWidth: 0 }}>
                            <Typography style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-text, #333)' }}>
                                {[client.contact_first_name, client.contact_last_name].filter(Boolean).join(' ') || client.contact_name || '—'}
                            </Typography>
                            {client.contact_title && (
                                <Typography style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                    {client.contact_title}
                                </Typography>
                            )}
                            <Typography style={{ fontSize: 12.5, color: ORANGE, wordBreak: 'break-word' }}>
                                {client.contact_email || '—'}
                            </Typography>
                        </Box>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                            {locationLabel(client)}
                        </Typography>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{client.classes_count ?? 0}</Typography>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{client.users}</Typography>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                            <span style={{ fontWeight: 700, color: 'var(--brand-text, #333)' }}>{client.programs_linked ?? 0}</span>
                            {/* The count alone — no "of X linked" (Ian-PW-02Oct-1 / Ian-SNW-02Oct-1). */}
                        </Typography>
                        <Box className={classes.desktopOnly}>
                            <LicensesCell used={client.licenses_used} total={client.licenses_total} small />
                        </Box>
                        <Box className={`${classes.desktopOnly} ${classes.netActions}`}>
                            {/*
                              * Publishing a network school is done from the header's bulk
                              * "Publish Workspace" button (which covers one school just as
                              * well as many) — a per-row button here was redundant and has
                              * been removed per review feedback.
                              */}
                            <Button
                                className={`${classes.outlineBtn} ${classes.netBtn}`}
                                onClick={() => handleOpenEdit(client)}
                            >
                                Edit Details
                            </Button>
                            <Button
                                className={`${classes.outlineBtn} ${classes.netBtn}`}
                                onClick={() => handleOpenAssign(client)}
                            >
                                Assign Licenses
                            </Button>
                            {/*
                              * Link Product — the network hands LearnCreator to ONE of its
                              * schools (the partner is refused for a school inside a network,
                              * so this is the only way that school gets it). Same flow as the
                              * partner's row; the row's institute_code targets the school.
                              */}
                            {canLinkProducts && (
                                <Tooltip title={networkLinkProductTooltip(client)} arrow>
                                    <span>
                                        <Button
                                            className={`${classes.outlineBtn} ${classes.netBtn}`}
                                            disabled={!networkHoldsLearnCreator || hasLearnCreator(client)}
                                            onClick={() => handleOpenLinkProducts(client)}
                                        >
                                            Link Product
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {/*
                              * Same "Link Programs" type-picker flow the partner's own row
                              * uses (handleOpenLinkPrograms → choose Learning Program or
                              * Standard Assessment) — not a standalone "Link Assessments"
                              * shortcut, per explicit instruction.
                              */}
                            <Tooltip
                                title={linkBlockedByLicenses(client)
                                    ? client.licenses_total === 0
                                        ? 'No licenses available. Please assign licenses first.'
                                        : 'Not enough licenses remaining.'
                                    // No hover text otherwise (Ian-SNW-19Oct-1).
                                    : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        className={`${classes.outlineBtn} ${classes.netBtn}`}
                                        disabled={linkBlockedByLicenses(client)}
                                        onClick={() => handleOpenLinkPrograms(client)}
                                    >
                                        Link Programs
                                    </Button>
                                </span>
                            </Tooltip>
                            {/*
                              * The per-school "School Dashboard" button was removed per
                              * review feedback — Routes.js has no per-school destination a
                              * network could open (/dq_pro/school-dashboard/* is scoped to
                              * the signed-in school), so a disabled stub was never a real
                              * action and the mockup marks it for removal, not disabling.
                              */}
                            <Button
                                className={`${classes.outlineBtn} ${classes.netBtn} ${classes.netBtnWide}`}
                                onClick={() => history.push('/dq_pro/classes')}
                            >
                                Manage Classes
                            </Button>
                        </Box>

                        {/* Mobile */}
                        <Box className={classes.mobilePrimary}>
                            <Typography className={classes.mobilePrimaryText}>{client.org_name}</Typography>
                            <AccountLifecycleBadge status={client.lifecycle_status} />
                        </Box>
                        <Box className={classes.mobileMetaRow}>
                            <span className={classes.mobileMetaTag}>{client.contact_email || '—'}</span>
                            <span className={classes.mobileMetaTag}>{locationLabel(client)}</span>
                            <span className={classes.mobileMetaTag}>{client.classes_count ?? 0} classes</span>
                            <span className={classes.mobileMetaTag}>{client.users} students</span>
                        </Box>
                        <Box className={classes.mobileActions} style={{ paddingTop: 8 }}>
                            <Button className={classes.outlineBtn} onClick={() => handleOpenEdit(client)}>Edit</Button>
                            {canLinkProducts && (
                                <Button
                                    className={classes.outlineBtn}
                                    disabled={!networkHoldsLearnCreator || hasLearnCreator(client)}
                                    onClick={() => handleOpenLinkProducts(client)}
                                >
                                    Link Product
                                </Button>
                            )}
                            <Button className={classes.primaryBtn} onClick={() => handleOpenAssign(client)}>Assign Licenses</Button>
                        </Box>
                    </Box>
                ))}

                {!viewerIsNetwork && visibleClients.length > 0 ? visibleClients.map((client, index) => (
                    <React.Fragment key={[client.org_code, client.institute_code].filter(Boolean).join('::') || index}>
                    <Box className={classes.row}>
                        {/*
                          * SCHOOL cell — two lines, as slide 33 draws it:
                          *
                          *     ▾ Horizon Education Group        [Published]
                          *       NETWORK  4 schools in 2 countries
                          *
                          * Everything on one line overflowed into CONTACT, because
                          * the badges cannot shrink and the grid track cannot grow.
                          */}
                        <Box className={classes.desktopOnly} style={{ minWidth: 0 }}>
                            {/* Wraps: the chevron and the PUBLISHED badge cannot shrink,
                                so on a narrow track an unwrapped row squeezed the NAME to a
                                few characters and broke it mid-word. Letting the badge drop
                                to its own line gives the name the full width instead. */}
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexWrap: 'wrap' }}>
                                {client.is_school_network && (
                                    <Tooltip title={expandedNetworks[client.org_code] ? 'Collapse schools' : 'Show schools'} arrow>
                                        <IconButton
                                            onClick={() => toggleNetwork(client.org_code)}
                                            aria-label={expandedNetworks[client.org_code] ? 'Collapse schools' : 'Show schools'}
                                            sx={{
                                                width: 32, height: 32, flexShrink: 0,
                                                color: '#fff', backgroundColor: ORANGE,
                                                boxShadow: '0 1px 4px rgba(255,105,0,0.45)',
                                                transform: expandedNetworks[client.org_code] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 120ms, background-color 120ms',
                                                '&:hover': { backgroundColor: '#e55a00' },
                                            }}
                                        >
                                            <ExpandMoreIcon sx={{ fontSize: 22 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Typography
                                    className={classes.cellTextLeft}
                                    style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'normal' }}
                                >
                                    {client.org_name}
                                </Typography>
                                <AccountLifecycleBadge status={client.lifecycle_status} />
                            </Box>

                            {/* NETWORK badge and its school count — slide 33. */}
                            {client.is_school_network && (
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, paddingLeft: 44 }}>
                                    <Box component="span" sx={{
                                        fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
                                        color: ORANGE, border: `1px solid ${ORANGE}`,
                                        borderRadius: "4px", px: 0.75, py: "1px",
                                        whiteSpace: "nowrap", flexShrink: 0,
                                    }}>NETWORK</Box>
                                    <Typography sx={{ fontSize: 12, color: "var(--brand-text-muted, #9CA3AF)" }}>
                                        {networkSummary(client)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {/* Contact: name / role / email stacked — slide 33. */}
                        <Box className={classes.desktopOnly}>
                            <Typography style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-text, #333)' }}>
                                {[client.contact_first_name, client.contact_last_name].filter(Boolean).join(' ') || client.contact_name || '—'}
                            </Typography>
                            {client.contact_title && (
                                <Typography style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                    {client.contact_title}
                                </Typography>
                            )}
                            <Typography style={{ fontSize: 12.5, color: ORANGE, wordBreak: 'break-word' }}>
                                {client.contact_email || '—'}
                            </Typography>
                        </Box>
                        {/* Location: a network spans its schools’ countries. */}
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                            {locationLabel(client)}
                        </Typography>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{client.users}</Typography>
                        {/* PROGRAMS: "2 of 4 linked" — slide 33. */}
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                            <span style={{ fontWeight: 700, color: 'var(--brand-text, #333)' }}>{client.programs_linked ?? 0}</span>
                        </Typography>
                        <Box className={classes.desktopOnly}>
                            <LicensesCell
                                used={client.licenses_used}
                                // No "allocated": licences are no longer assigned to classes, they
                                // are used when learners are published (Ian-PW-29Sep-4).
                                total={client.licenses_total}
                            />
                        </Box>
                        <Box className={`${classes.desktopOnly} ${classes.actionStack}`}>
                            <Tooltip title={ACTION_TOOLTIPS.editDetails} arrow>
                                <Button className={classes.outlineBtn} onClick={() => handleOpenEdit(client)}>
                                    Edit Details
                                </Button>
                            </Tooltip>
                            {isAccountDraft(client.lifecycle_status) && (
                                <Tooltip title={ACTION_TOOLTIPS.publish} arrow>
                                    <Button className={classes.outlineBtn} onClick={() => handleOpenPublish(client)}>
                                        Publish Workspace
                                    </Button>
                                </Tooltip>
                            )}
                            {canLinkProducts && (
                                <Tooltip
                                    title={hasLearnCreator(client)
                                        ? `LearnCreator is already linked to ${client.org_name}.`
                                        : ACTION_TOOLTIPS.linkProducts}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            className={classes.outlineBtn}
                                            disabled={hasLearnCreator(client)}
                                            onClick={() => handleOpenLinkProducts(client)}
                                        >
                                            Link Product
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {/* Slide 34, button 5 — opens the Learning Program /
                                Standard Assessment type-picker (no LearnCreator
                                card; that's "Link Products" above). Licenses are
                                needed for either type, so that alone gates the
                                button; the Learning Program permission gates only
                                its own card inside the dialog. */}
                            <Tooltip
                                title={linkBlockedByLicenses(client)
                                    ? client.licenses_total === 0
                                        ? 'No licenses available. Please assign licenses first.'
                                        : 'Not enough licenses remaining.'
                                    : ACTION_TOOLTIPS.linkPrograms}
                                arrow
                            >
                                <span>
                                    <Button
                                        className={classes.outlineBtn}
                                        disabled={linkBlockedByLicenses(client)}
                                        onClick={() => handleOpenLinkPrograms(client)}
                                    >
                                        Link Programs
                                    </Button>
                                </span>
                            </Tooltip>
                            {/* Last, and white like the rest — LA-PW-07-Oct-03, slide 17. */}
                            <Tooltip title={ACTION_TOOLTIPS.assignLicenses} arrow>
                                <Button className={classes.outlineBtn} onClick={() => handleOpenAssign(client)}>
                                    Assign Licenses &amp; Credits
                                </Button>
                            </Tooltip>
                        </Box>
                        {/* Mobile card */}
                        <Box className={classes.mobilePrimary}>
                            <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Typography className={classes.mobilePrimaryText}>{client.org_name}</Typography>
                                <AccountLifecycleBadge status={client.lifecycle_status} />
                            </Box>
                        </Box>
                        <Box className={classes.mobileMetaRow}>
                            <span className={classes.mobileMetaTag}>{client.contact_email || '—'}</span>
                            <span className={classes.mobileMetaTag}>{countryCodeToName[client.country] || client.country || '—'}</span>
                            <span className={classes.mobileMetaTag}>{client.users} users</span>
                            <span className={classes.mobileMetaTag}>
                                {/* used / alloc / total */}
                                <span style={{ fontWeight: 700, color: ORANGE }}>{client.licenses_used}</span>
                                {' used / '}{client.licenses_total}{' total'}
                            </span>
                        </Box>
                        <Box className={classes.mobileActions} style={{ paddingTop: '8px' }}>
                            <Tooltip title={ACTION_TOOLTIPS.editDetails} arrow>
                                <Button className={classes.outlineBtn} onClick={() => handleOpenEdit(client)}>
                                     Edit Details
                                 </Button>
                            </Tooltip>
                            {isAccountDraft(client.lifecycle_status) && (
                                <Tooltip title={ACTION_TOOLTIPS.publish} arrow>
                                    <Button className={classes.outlineBtn} onClick={() => handleOpenPublish(client)}>
                                        Publish Workspace
                                    </Button>
                                </Tooltip>
                            )}
                            {canLinkProducts && (
                                <Tooltip
                                    title={hasLearnCreator(client)
                                        ? `LearnCreator is already linked to ${client.org_name}.`
                                        : ACTION_TOOLTIPS.linkProducts}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            className={classes.outlineBtn}
                                            disabled={hasLearnCreator(client)}
                                            onClick={() => handleOpenLinkProducts(client)}
                                        >
                                            Link Product
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {/* Slide 34, button 5 — opens the Learning Program /
                                Standard Assessment type-picker (no LearnCreator
                                card; that's "Link Products" above). Licenses are
                                needed for either type, so that alone gates the
                                button; the Learning Program permission gates only
                                its own card inside the dialog. */}
                            <Tooltip
                                title={linkBlockedByLicenses(client)
                                    ? client.licenses_total === 0
                                        ? 'No licenses available. Please assign licenses first.'
                                        : 'Not enough licenses remaining.'
                                    : ACTION_TOOLTIPS.linkPrograms}
                                arrow
                            >
                                <span>
                                    <Button
                                        className={classes.outlineBtn}
                                        disabled={linkBlockedByLicenses(client)}
                                        onClick={() => handleOpenLinkPrograms(client)}
                                    >
                                        Link Programs
                                    </Button>
                                </span>
                            </Tooltip>
                            {/* Last, and white like the rest — LA-PW-07-Oct-03, slide 17. */}
                            <Tooltip title={ACTION_TOOLTIPS.assignLicenses} arrow>
                                <Button className={classes.outlineBtn} onClick={() => handleOpenAssign(client)}>
                                    Assign Licenses &amp; Credits
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/*
                      * "Schools in <network>" — slide 33's expanded panel. The
                      * schools come down on the network row itself, so opening
                      * this costs no request. They are deliberately NOT rows of
                      * their own in the list above.
                      */}
                    {client.is_school_network && expandedNetworks[client.org_code] && (
                        <Box className={classes.networkPanel}>
                            <Box className={classes.networkPanelHead}>
                                <Typography style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                    Schools in {client.org_name}
                                </Typography>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Typography style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                        {networkSummary(client)}
                                    </Typography>
                                    <Button className={classes.outlineBtn} onClick={() => setAddToNetwork(client)}>
                                        + Add a school
                                    </Button>
                                </Box>
                            </Box>
                            {(client.schools || []).length > 0 && (
                                <Box className={classes.networkPanelRow} style={{ borderTop: 'none', paddingTop: 0 }}>
                                    <Typography className={classes.panelHeadCell} style={{ textAlign: 'left' }}>School</Typography>
                                    <Typography className={classes.panelHeadCell} style={{ textAlign: 'left' }}>Contact</Typography>
                                    <Typography className={classes.panelHeadCell}>Location</Typography>
                                    <Typography className={classes.panelHeadCell}>Students</Typography>
                                    <Typography className={classes.panelHeadCell}>Programs</Typography>
                                    <Typography className={classes.panelHeadCell}>Licenses</Typography>
                                </Box>
                            )}
                            {(client.schools || []).length === 0 ? (
                                <Typography style={{ fontSize: 13, color: 'var(--brand-text-muted, #9CA3AF)', padding: '12px 4px' }}>
                                    No schools in this network yet.
                                </Typography>
                            ) : (client.schools || []).map((sc) => (
                                /* Keyed by institute_code, not org_code: every school of a
                                   network now SHARES that network's org_code, so org_code is
                                   the same string on every sibling and React would see one
                                   duplicated key instead of a list. */
                                <Box key={sc.institute_code || sc.org_code} className={classes.networkPanelRow}>
                                    {/*
                                      * Status is SHOWN here but not actionable.
                                      *
                                      * A school inside a network does NOT go live with its
                                      * network — the network publishes each of its schools
                                      * itself, from its own workspace. So the partner needs to
                                      * see which are live (a network can sit published for
                                      * weeks with half its schools still drafts) while having
                                      * no Publish control: that decision is the network's.
                                      * The server enforces the same rule — see
                                      * resolveOrgCodeForActor's `as_publisher`.
                                      */}
                                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
                                        <Typography style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-text, #333)', minWidth: 0, wordBreak: 'break-word' }}>
                                            {sc.org_name}
                                        </Typography>
                                        <AccountLifecycleBadge status={sc.lifecycle_status} />
                                    </Box>
                                    <Box>
                                        <Typography style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-text, #333)' }}>
                                            {[sc.contact_first_name, sc.contact_last_name].filter(Boolean).join(' ') || sc.contact_name || '—'}
                                        </Typography>
                                        {sc.contact_title && (
                                            <Typography style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                {sc.contact_title}
                                            </Typography>
                                        )}
                                        <Typography style={{ fontSize: 12, color: ORANGE, wordBreak: 'break-word' }}>
                                            {sc.contact_email || '—'}
                                        </Typography>
                                    </Box>
                                    <Typography style={{ fontSize: 13, color: 'var(--brand-text, #444)', textAlign: 'center' }}>
                                        {countryCodeToName[sc.country] || sc.country || '—'}
                                    </Typography>
                                    <Typography style={{ fontSize: 13, color: 'var(--brand-text, #444)', textAlign: 'center' }}>
                                        {sc.users ?? 0}
                                    </Typography>
                                    <Typography style={{ fontSize: 13, color: 'var(--brand-text, #444)', textAlign: 'center' }}>
                                        <span style={{ fontWeight: 700 }}>{sc.programs_linked ?? 0}</span>
                                    </Typography>
                                    <LicensesCell used={sc.licenses_used} total={sc.licenses_total} small />
                                </Box>
                            ))}
                        </Box>
                    )}
                    </React.Fragment>
                )) : (!viewerIsNetwork && listLoaded) ? (
                    <Box style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        borderRadius: '16px',
                        border: '1px dashed rgba(241, 90, 36, 0.3)',
                        // Was 1120px, wider than a 1366px laptop screen leaves beside the menu.
        minWidth: '820px',
                    }}>
                        {/*
                          * Slide 33 draws a small OUTLINED mark inside a dashed
                          * rounded square — not a large filled one.
                          */}
                        <Box style={{
                            width: 52, height: 52, borderRadius: 12, marginBottom: 18,
                            border: '1px dashed var(--brand-border, #D8D2CC)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <HomeOutlinedIcon style={{ fontSize: 24, color: 'var(--brand-text-muted, #B7AFA7)' }} />
                        </Box>
                        <Typography style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #333)', marginBottom: 8 }}>
                            {search
                                ? (isEnterprisePlan ? 'No matching schools' : 'No matching clients')
                                : (isEnterprisePlan ? 'No schools yet' : 'No clients yet')}
                        </Typography>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', textAlign: 'center', maxWidth: 400 }}>
                            {search
                                ? (isEnterprisePlan
                                    ? `No schools match "${search}". Try a different search.`
                                    : `No clients match "${search}". Try a different search.`)
                                : (isEnterprisePlan
                                    // PW deck slide 33. Its own words, with one
                                    // change: the slide says "They'll get an
                                    // activation email", which Ian's note on
                                    // slides 35 and 38 moves to publication.
                                    ? "Add a single school, or a network if the schools belong to one group. They'll get an activation email when you publish their workspace. You'll need credits before you can assign any licenses to them."
                                    : 'You haven\'t added any clients. Click "Add a New Client" to get started.')}
                        </Typography>
                        {/*
                          * ONE entry point, with the same label as the header —
                          * slide 33's empty state. The School / Network choice is
                          * made inside the dialog, not out here.
                          */}
                        {isEnterprisePlan && !search && (
                            <Box style={{ marginTop: 20 }}>
                                <Button className={classes.addBtn} onClick={() => openCreate(false)}>
                                    {viewerIsNetwork ? '+ Add a School' : '+ Add a School or Network'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </Box>

            {/*
              * Pinned to the bottom of the page (above the footer) regardless of
              * how little table content there is — the root Box is flexGrow:1
              * flex-column, so mt:'auto' here consumes whatever space the table
              * didn't fill, instead of leaving a gap before the footer.
              */}
            <Box sx={{ mt: 'auto' }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />

                {/*
                  * Credits strip — slides 33-34. Only rendered once the balance
                  * has actually been read; a partner whose plan prices licences
                  * per contract rather than at the flat rate is left out, since
                  * "3 credits = 1 license" would not be true for them.
                  */}
                {assignsWithTokens && (
                    <Box style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 12,
                        marginTop: 16, padding: '16px 20px', borderRadius: 16,
                        backgroundColor: 'var(--brand-surface, #fff)',
                        border: '1px solid rgba(241, 90, 36, 0.18)',
                    }}>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text, #444)' }}>
                            {'Your DQ Tokens: '}
                            <strong>{Number(dqtWallet.dqt_balance || 0).toLocaleString()} DQT</strong>
                            {dqtWallet.licence_price_dqt != null && <>{' · 1 license = '}<strong>{Number(dqtWallet.licence_price_dqt).toLocaleString()} DQT</strong></>}
                            {dqtWallet.credit_price_dqt != null && <>{' · 1 credit = '}<strong>{Number(dqtWallet.credit_price_dqt).toLocaleString()} DQT</strong></>}
                        </Typography>
                        <Button
                            className={classes.outlineBtn}
                            onClick={() => history.push('/account/settings?topup=1')}
                        >
                            + Buy more DQ Tokens
                        </Button>
                    </Box>
                )}
                {!assignsWithTokens && !viewerIsNetwork && creditBalance !== null && creditsPerLicense > 0 && (
                    <Box style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 12,
                        marginTop: 16, padding: '16px 20px', borderRadius: 16,
                        backgroundColor: 'var(--brand-surface, #fff)',
                        border: '1px solid rgba(241, 90, 36, 0.18)',
                    }}>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text, #444)' }}>
                            {'Your available DQ Tokens: '}
                            <strong>{creditBalance.toLocaleString()} DQ Tokens</strong>
                            {', redeemable for '}
                            <strong>{redeemableLicenses.toLocaleString()} licenses</strong>
                        </Typography>
                        <Button
                            className={classes.outlineBtn}
                            onClick={() => history.push('/account/settings?topup=1')}
                        >
                            + Buy more DQ Tokens
                        </Button>
                    </Box>
                )}
            </Box>

            <LoaderWithBackDrop loading={loading} />
            {/* Partner Workspace only: the floating "?" and its five-step guide
                (LA-PW-07-Oct-03, slides 18-23). */}
            {!viewerIsNetwork && isEnterprisePlan && <MySchoolsGuide />}
            {viewerIsNetwork && <MySchoolsGuide network />}
            <CreateClientDialog
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                onSubmit={handleCreateClient}
                loading={creating}
                isSchool={isEnterprisePlan}
                initialIsNetwork={createAsNetwork}
                allowNetwork={!viewerIsNetwork}
                onUpload={canBulkUpload ? openBulkUploadFromAdd : undefined}
                uploadLabel={bulkUploadLabel}
                manualLabel="Add Manually"
            />
            <EditClientDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onSubmit={handleEditClient}
                loading={editing}
                client={editingClient}
                viewerIsNetwork={viewerIsNetwork}
            />
            <AddSchoolToNetworkDialog
                open={!!addToNetwork}
                onClose={() => setAddToNetwork(null)}
                onSubmit={handleAddSchoolToNetwork}
                loading={addingToNetwork}
                network={addToNetwork}
                viewerIsNetwork={viewerIsNetwork}
                // The network's own Add only — a partner adding one school to a
                // network from its row keeps the plain form.
                onUpload={viewerIsNetwork ? openBulkUploadFromAdd : undefined}
                uploadLabel={bulkUploadLabel}
                manualLabel="Add Manually"
            />
            {/* Always mounted: an upload keeps running while its window is closed. */}
            {(viewerIsNetwork || isEnterprisePlan) && (
                <BulkUploadSchoolsDialog
                    open={bulkUploadOpen}
                    onClose={() => setBulkUploadOpen(false)}
                    onReopen={() => setBulkUploadOpen(true)}
                    kind={viewerIsNetwork ? UPLOAD_KIND_NETWORK : UPLOAD_KIND_PARTNER}
                    onChanged={() => fetchClients()}
                    onManual={openAddFromBulkUpload}
                />
            )}
            {viewerIsNetwork && (
                <BulkPublishSchoolsDialog
                    open={bulkPublishOpen}
                    onClose={() => setBulkPublishOpen(false)}
                    // The pop-up shows its own "Workspaces Published" step now
                    // (LA-SNW-07-Oct-3, slide 43); only a run where nothing was
                    // published is reported here.
                    onPublished={({ succeeded, failed }) => {
                        fetchClients();
                        if (!succeeded.length && failed.length) {
                            toast(TOAST_TYPES.WARN, 'Publish workspaces', `${failed.length} school${failed.length === 1 ? '' : 's'} could not be published.`);
                        }
                    }}
                    onResend={handleResendActivation}
                    schools={visibleClients.map((c) => ({
                        org_code: c.org_code,
                        // Siblings share org_code; this is what names one of them.
                        institute_code: c.institute_code,
                        org_name: c.org_name,
                        contact_email: c.contact_email,
                        students: c.users,
                        classes: c.classes_count,
                        isPublished: isAccountPublished(c.lifecycle_status),
                    }))}
                    publishOne={async (org_code, institute_code) => {
                        const res = await publishClientService(org_code, { institute_code });
                        return res?.data || { success: false };
                    }}
                />
            )}
            <AssignWithTokensDialog
                open={assignDialogOpen && assignsWithTokens}
                onClose={() => setAssignDialogOpen(false)}
                org={assignOrg}
                onBuyTokens={() => history.push('/account/settings?topup=1')}
                onAssigned={() => {
                    fetchClients();
                    getDqtWalletService().then((res) => { if (res?.data) setDqtWallet(res.data); }).catch(() => {});
                }}
            />
            <AssignLicensesDialog
                open={assignDialogOpen && !assignsWithTokens}
                onClose={() => setAssignDialogOpen(false)}
                onSubmit={handleAssignLicenses}
                loading={assigning}
                orgName={assignOrg.org_name}
                orgCode={assignOrg.org_code}
                isSchool={isEnterprisePlan}
                {...(viewerIsNetwork
                    ? { fromNetworkPool: true, poolAvailable: networkPoolAvailable, targetTotal: assignOrg.licenses_total, targetUsed: assignOrg.licenses_used }
                    : {
                        tokenBalance: creditBalance,
                        tokensPerLicense: creditsPerLicense,
                        onBuyMoreTokens: () => history.push('/account/settings?topup=1'),
                    })}
            />
            <LinkAssessmentDialog
                assessments={assessments}
                open={linkDialogOpen}
                onSubmit={handleLinkAssessments}
                loading={linking}
                onClose={() => setLinkDialogOpen(false)}
                orgName={linkOrg.org_name}
                orgCode={linkOrg.org_code}
                linkedAssessmentCodes={linkedAssessments}
                onLinked={fetchClients} // optional, if you want to refresh list
                isSchool={isEnterprisePlan}
                targetNoun={linkTargetNoun(linkOrg)}
            />
            <ConfirmPublishDialog
                open={!!publishTarget}
                onClose={handleClosePublishFlow}
                onConfirm={handleConfirmPublish}
                loading={publishing}
                orgName={publishTarget?.org_name}
                contactEmail={publishTarget?.contact_email}
            />
            <PublishWorkspaceSuccessDialog
                open={!!publishResult}
                onClose={() => setPublishResult(null)}
                onResend={() => handleResendActivation(publishResult?.org_code, publishResult?.institute_code)}
                resending={resending}
                orgName={publishResult?.org_name}
                contactEmail={publishResult?.contact_email}
                publishedAt={publishResult?.published_at}
                credentialsEmailed={publishResult?.credentials_emailed}
                isSchool={isEnterprisePlan}
            />
            <LinkProgramDialog
                programs={learningPrograms}
                open={linkLearningDialogOpen}
                onSubmit={handleLinkLearningPrograms}
                loading={linkingLearning}
                onClose={() => setLinkLearningDialogOpen(false)}
                orgName={learningOrg.org_name}
                orgCode={learningOrg.org_code}
                linkedProgramCodes={linkedLearningCodes}
                linkingLoading={linkLearningLoading}
                targetNoun={linkTargetNoun(learningOrg)}
            />
            <LinkTypePickerDialog
                open={linkProductsDialogOpen}
                onClose={() => setLinkProductsDialogOpen(false)}
                onChoose={handleChooseProductType}
                planKey={authUser?.dq_pro_plan?.plan_key}
                orgName={linkProductsClient?.org_name}
                cardKeys={[LINK_TYPE.LEARN_CREATOR]}
                assignNoun="product"
                unavailableReasons={linkProductsUnavailableReasons}
            />
            <LinkTypePickerDialog
                open={linkProgramsDialogOpen}
                onClose={() => setLinkProgramsDialogOpen(false)}
                onChoose={handleChooseProgramType}
                planKey={authUser?.dq_pro_plan?.plan_key}
                orgName={linkProgramsClient?.org_name}
                cardKeys={[LINK_TYPE.LEARNING_PROGRAM, LINK_TYPE.STANDARD_ASSESSMENT]}
                targetNoun={linkTargetNoun(linkProgramsClient)}
                unavailableReasons={canLinkLearningProgram ? {} : {
                    [LINK_TYPE.LEARNING_PROGRAM]: 'The Learning Program product is not enabled for your account.',
                }}
            />
            <AssignProductDialog
                open={assignProductDialogOpen}
                onClose={() => setAssignProductDialogOpen(false)}
                onProceed={handleProceedToConfirmAssign}
                productLabel="LearnCreator"
                org={linkProductsClient && {
                    name: linkProductsClient.org_name,
                    isNetwork: !!linkProductsClient.is_school_network,
                    subtitle: linkProductsClient.is_school_network
                        ? `${locationLabel(linkProductsClient)} · ${(linkProductsClient.schools || []).length} school${(linkProductsClient.schools || []).length === 1 ? '' : 's'}`
                        : locationLabel(linkProductsClient),
                    contactEmail: linkProductsClient.contact_email,
                }}
            />
            <ConfirmAssignmentDialog
                open={confirmAssignOpen}
                onCancel={handleCancelConfirmAssign}
                onConfirm={handleConfirmAssignment}
                loading={assigningProduct}
                productLabel="LearnCreator"
                orgName={linkProductsClient?.org_name}
            />
        </Box>
    );
};

export default DQProClientList;
